import { Download, FileText, Mail, Send } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Modal } from '@/components/ui/modal'
import { Select } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { CheckRow, Field, FormSection } from '@/components/common/FormBits'
import { useAuth } from '@/lib/auth/AuthProvider'
import { fmtDateTime } from '../../../shared/format'
import { downloadMinutes, MINUTES_PERMS as P, useMinutesSends, useSendMinutes, type MinutesVersion } from '../../meetingFiles'
import type { Meeting } from '../../types'
import { toastError, useCrm } from '../../ui'

type Contact = { id: string; name: string; email: string }

/** Tutanak: iç / müşteri sürümünü PDF ya da Word indirme, e-postayla dağıtma ve gönderim geçmişi. */
export function MeetingMinutesDoc({ meeting: m, contacts }: { meeting: Meeting; contacts: Contact[] }) {
  const { t } = useTranslation()
  const { can } = useAuth()
  const { staffName } = useCrm()
  const [sending, setSending] = useState(false)
  const canExport = can(P.export)
  const canSend = can(P.distribute)
  const sends = useMinutesSends(m.id, canSend || canExport)
  if (!canExport && !canSend) return null
  const dl = (v: MinutesVersion, f: 'pdf' | 'docx') => void downloadMinutes(m.id, v, f).catch(toastError)
  return (
    <section className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-1.5 text-sm font-semibold">
          <FileText className="size-4 text-primary" aria-hidden />
          {t('crm.minutesDoc.title')}
        </h3>
        <div className="flex gap-1.5">
          {canExport ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="outline">
                  <Download />
                  {t('crm.minutesDoc.download')}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-52">
                {(['internal', 'customer'] as const).map((v, i) => (
                  <div key={v}>
                    {i ? <DropdownMenuSeparator /> : null}
                    <DropdownMenuLabel>{t(`crm.minutesDoc.versions.${v}`)}</DropdownMenuLabel>
                    <DropdownMenuItem onSelect={() => dl(v, 'pdf')}>PDF</DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => dl(v, 'docx')}>Word (.docx)</DropdownMenuItem>
                  </div>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
          {canSend ? (
            <Button size="sm" variant="outline" onClick={() => setSending(true)}>
              <Send />
              {t('crm.minutesDoc.send')}
            </Button>
          ) : null}
        </div>
      </div>
      {sends.data?.length ? (
        <ul className="space-y-1.5">
          {sends.data.map((x) => (
            <li key={x.id} className="rounded-lg bg-muted/40 px-3 py-2 text-xs">
              <p className="flex flex-wrap items-center gap-1.5">
                <Badge tone={x.version === 'customer' ? 'info' : 'neutral'}>{t(`crm.minutesDoc.versions.${x.version}`)}</Badge>
                <span className="font-medium">{staffName(x.sentBy)}</span>
                <span className="text-muted-foreground tabular-nums">{fmtDateTime(x.createdAt)}</span>
                {x.channel === 'system' ? <span className="text-muted-foreground">· {t('crm.minutesDoc.systemChannel')}</span> : null}
              </p>
              <p className="mt-0.5 break-all text-muted-foreground">{x.recipients.join(', ')}</p>
            </li>
          ))}
        </ul>
      ) : null}
      {sending ? <SendModal meeting={m} contacts={contacts} onClose={() => setSending(false)} /> : null}
    </section>
  )
}

function SendModal({ meeting: m, contacts, onClose }: { meeting: Meeting; contacts: Contact[]; onClose: () => void }) {
  const { t } = useTranslation()
  const { can, user } = useAuth()
  const { staff } = useCrm()
  const send = useSendMinutes(m.id)
  const isPrivate = m.visibility === 'private'
  const participants = [m.ownerId, ...m.staffIds]
  const staffOptions = staff.filter((s) => s.id !== user?.id && (!isPrivate || participants.includes(s.id)))
  const canCustomer = can(P.distributeCustomer)
  const [managers, setManagers] = useState(!isPrivate)
  const [staffIds, setStaffIds] = useState<string[]>(m.staffIds.filter((id) => id !== user?.id))
  const [contactIds, setContactIds] = useState<string[]>([])
  const [guestEmails, setGuestEmails] = useState<string[]>([])
  const [extra, setExtra] = useState('')
  const [message, setMessage] = useState('')
  const [format, setFormat] = useState<'pdf' | 'docx'>('pdf')
  const guests = m.guests.filter((g) => g.email)
  const mailable = contacts.filter((c) => c.email)
  const toggle = (list: string[], set: (v: string[]) => void, id: string) => set(list.includes(id) ? list.filter((x) => x !== id) : [...list, id])
  const extraEmails = extra
    .split(/[,;\s]+/)
    .map((x) => x.trim())
    .filter(Boolean)
  const badExtra = extraEmails.some((e) => !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e))
  const customerCount = contactIds.length + guestEmails.length + extraEmails.length
  const none = !managers && !staffIds.length && !customerCount

  const submit = () =>
    send.mutate(
      { managers, staffIds, contactIds, guestEmails, extraEmails, message, format },
      {
        onSuccess: (r) => {
          toast.success(t('crm.minutesDoc.sent', { count: r.internal.length + r.customer.length }))
          if (r.internalChannel === 'system') toast.info(t('crm.minutesDoc.systemFallback'))
          onClose()
        },
        onError: toastError,
      },
    )

  return (
    <Modal
      open
      size="lg"
      onOpenChange={(v) => !v && onClose()}
      icon={<Mail />}
      title={t('crm.minutesDoc.sendTitle')}
      description={t('crm.minutesDoc.sendHint')}
      footer={
        <>
          <Button variant="cancel" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={submit} disabled={none || badExtra || send.isPending}>
            <Send />
            {t('crm.minutesDoc.send')}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <FormSection title={t('crm.minutesDoc.internalSection')}>
          {isPrivate ? <p className="text-xs text-warning">{t('crm.minutesDoc.privateHint')}</p> : null}
          {!isPrivate ? (
            <CheckRow checked={managers} onChange={(e) => setManagers(e.target.checked)} hint={t('crm.minutesDoc.managersHint')}>
              {t('crm.minutesDoc.managers')}
            </CheckRow>
          ) : null}
          <div className="flex flex-wrap gap-1.5">
            {staffOptions.map((s) => (
              <Button key={s.id} type="button" size="sm" variant={staffIds.includes(s.id) ? 'default' : 'outline'} onClick={() => toggle(staffIds, setStaffIds, s.id)}>
                {s.name}
              </Button>
            ))}
          </div>
        </FormSection>
        {canCustomer ? (
          <FormSection title={t('crm.minutesDoc.customerSection')}>
            <p className="text-xs text-muted-foreground">{t('crm.minutesDoc.customerHint')}</p>
            {mailable.length || guests.length ? (
              <div className="flex flex-wrap gap-1.5">
                {mailable.map((c) => (
                  <Button key={c.id} type="button" size="sm" variant={contactIds.includes(c.id) ? 'default' : 'outline'} onClick={() => toggle(contactIds, setContactIds, c.id)}>
                    {c.name} · {c.email}
                  </Button>
                ))}
                {guests.map((g) => (
                  <Button key={g.email} type="button" size="sm" variant={guestEmails.includes(g.email) ? 'default' : 'outline'} onClick={() => toggle(guestEmails, setGuestEmails, g.email)}>
                    {g.name} · {g.email}
                  </Button>
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">{t('crm.minutesDoc.noCustomerEmails')}</p>
            )}
            <Field id="ms-extra" label={t('crm.minutesDoc.extra')} error={badExtra ? t('crm.minutesDoc.extraInvalid') : undefined}>
              <Input id="ms-extra" value={extra} placeholder="ad@firma.com, ..." onChange={(e) => setExtra(e.target.value)} />
            </Field>
          </FormSection>
        ) : null}
        <Field id="ms-message" label={t('crm.minutesDoc.message')} hint={t('crm.minutesDoc.messageHint')}>
          <Textarea id="ms-message" rows={4} maxLength={4000} value={message} onChange={(e) => setMessage(e.target.value)} />
        </Field>
        <Field id="ms-format" label={t('crm.minutesDoc.format')}>
          <Select id="ms-format" value={format} onChange={(v) => setFormat(v as 'pdf' | 'docx')} options={[{ value: 'pdf', label: 'PDF' }, { value: 'docx', label: 'Word (.docx)' }]} />
        </Field>
        {none ? <p className="text-xs text-muted-foreground">{t('crm.minutesDoc.pickRecipients')}</p> : null}
      </div>
    </Modal>
  )
}
